import {get} from 'object-path-immutable'
import React, {useCallback, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {NavLink, Navigate, Routes} from 'react-router-dom'

import {gql} from '@apollo/client'
import {isRight} from 'fp-ts/lib/Either'
import memoizeOne from 'memoize-one'
import {getInactivityTimeout} from '../../app'
import {
  OrganisationPreferences,
  OrganisationPreferencesCodec
} from '../../common/types'
import {useSetCrumbs} from '../../components/crumbtrail'
import {BetaFlagMarker, PreviewFlagMarker} from '../../components/feature-flags'
import {Form, FormSpacer} from '../../components/form'
import {TextInput} from '../../components/library/form'
import {NumberInput} from '../../components/library/form/number-input'
import {Icon} from '../../components/library/icon/icon'
import {NoResultsPermissions} from '../../components/library/list'
import styled from 'styled-components'
import {Switch} from '../../components/library/toggles/switch'
import {Loader} from '../../components/loader'
import {FormModal} from '../../components/modals'
import {useGetOrganisationQuery} from '../../data/workbench-api/generated/organisation'
import {
  PermissionAction,
  PermissionResource
} from '../../data/workbench-api/generated/types'
import {Flags, useFlags} from '../../hooks/use-flags'
import {useStateChanged} from '../../hooks/use-state-changed'
import {useAnalytics} from '../../lib/analytics/analytics'
import {useAuth} from '../../lib/auth'
import {Route} from '../../lib/router'
import {useToast} from '../../lib/toast'
import {OrganisationNode} from '../../organisation'
import {
  Content,
  HeaderBox,
  IconHeading,
  Main,
  Menu
} from '../configuration/components'
import {ConstrainWidth} from './components'
import {DataRetention} from './data-retention'
import {
  useManageOrganisationUpdateNameMutation,
  useOrganisationManagePreferencesMutation
} from './generated'
import {ManageSSOAuth} from './sso'

const LightText = styled.p`
  color: ${props => props.theme.grey50};
`

const getLinkGroups = memoizeOne((flags?: Flags) =>
  [
    {
      group: 'General',
      links: [
        {to: 'organization', label: 'Name'},
        {to: 'data-retention', label: 'Data retention'}
      ]
    },
    {
      group: 'Authentication',
      links: [
        {to: 'authentication', label: 'Authentication preferences'},
        {to: 'auth-sso', label: 'Single sign-on', flag: 'beta'}
      ]
    },
    {
      group: 'Tools',
      links: [{to: 'sdk-request-debugger', label: 'SDK request debugger'}]
    }
  ].map(group => ({
    ...group,
    links: group.links.filter(
      link => link.flag !== 'preview' || flags?.featurePreview
    )
  }))
)

const ConfirmationModal = ({
  name,
  mfaRequired,
  onCancel,
  onSubmit
}: {
  name: string
  mfaRequired: boolean
  onCancel: () => void
  onSubmit: () => void
}) => {
  const {authState} = useAuth()
  return (
    <FormModal
      title={`${
        mfaRequired ? 'Enable' : 'Disable'
      } Multi-factor authentication`}
      handleClose={onCancel}
      onSubmit={onSubmit}
      submitText={mfaRequired ? 'Enable' : 'Disable'}
    >
      {!mfaRequired ||
      ('mfaEnabled' in authState && authState.mfaEnabled) ? null : (
        <div style={{marginBottom: '20px'}}>
          You do not have MFA enabled, this will lock you out.
        </div>
      )}
      <div>
        {mfaRequired ? (
          <>
            Workbench members will be required to have Multi-factor
            authentication enabled to log into <b>{name}</b>.
          </>
        ) : (
          <>
            Workbench members will be able to log into <b>{name}</b> without
            requiring Multi-factor authentication.
          </>
        )}
      </div>
    </FormModal>
  )
}

export const OrgPreferences = ({
  organisation
}: {
  organisation: OrganisationNode
}) => {
  const flags = useFlags(organisation.id)

  const canView = organisation.hasPermission(
    PermissionResource.Organisation,
    PermissionAction.View
  )
  const canEdit = organisation.hasPermission(
    PermissionResource.Organisation,
    PermissionAction.Edit
  )
  const canAdmin =
    canEdit &&
    organisation.hasPermission(
      PermissionResource.Organisation,
      PermissionAction.Admin
    )

  useSetCrumbs(['Manage organization'])

  return canView ? (
    <>
      <Helmet>
        <title>Manage organization</title>
      </Helmet>
      <HeaderBox>
        <h2>Manage organization</h2>
      </HeaderBox>
      <Content>
        <Menu>
          {getLinkGroups(flags).map(group => (
            <React.Fragment key={group.group}>
              <h3>{group.group}</h3>
              <ul>
                {group.links.map(link => (
                  <li key={link.to}>
                    <NavLink to={link.to}>
                      {link.label}{' '}
                      {link.flag === 'preview' && <PreviewFlagMarker />}
                      {link.flag === 'beta' && <BetaFlagMarker />}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          ))}
        </Menu>

        <Main>
          {!organisation ? (
            <Loader />
          ) : (
            <Routes>
              <Route
                path="/"
                render={() => <Navigate to="organization" replace />}
              />
              {/* TODO remove redirect from old route after a few weeks */}
              <Route
                path="/organisation"
                render={() => <Navigate to="../organization" replace />}
              />
              <Route
                path="/organization"
                render={() => (
                  <ManageOrganisation
                    organisationId={organisation.id}
                    canEdit={canEdit}
                  />
                )}
              />
              <Route
                path="/data-retention"
                render={() => (
                  <DataRetention
                    organisationId={organisation.id}
                    canEdit={canAdmin}
                  />
                )}
              />
              <Route
                path="/auth-username"
                render={() => <Navigate to="authentication" replace />}
              />
              <Route
                path="authentication"
                render={() => (
                  <ManageAuthentication
                    organisationId={organisation.id}
                    canEdit={canEdit}
                  />
                )}
              />
              <Route
                path="auth-sso"
                render={() => (
                  <ManageSSOAuth
                    organisationId={organisation.id}
                    canView={canView}
                    canEdit={canEdit}
                  />
                )}
              />
              <Route
                path="sdk-request-debugger"
                render={() => (
                  <ManageSDKRequestDebugger
                    organisationId={organisation.id}
                    canEdit={canEdit}
                  />
                )}
              />
            </Routes>
          )}
        </Main>
      </Content>
    </>
  ) : (
    <NoResultsPermissions />
  )
}

const ManageSDKRequestDebugger = ({
  organisationId,
  canEdit
}: {
  organisationId: string
  canEdit: boolean
}) => {
  const toaster = useToast()
  const orgQuery = useGetOrganisationQuery({variables: {id: organisationId}})

  const [localState, setLocalState, changed, resetState] = useStateChanged<{
    preferences: OrganisationPreferences
  }>({
    preferences: {}
  })

  const [doMutation, {loading, error}] =
    useOrganisationManagePreferencesMutation({
      onCompleted: () => toaster('Organization settings updated')
    })

  const update = (save: OrganisationPreferences) =>
    doMutation({
      variables: {
        id: organisationId,
        updates: save
      }
    })

  const setFormState = useCallback(
    (data: (typeof orgQuery)['data']) => {
      if (data?.organisation?.name) {
        const preferencesCodec = OrganisationPreferencesCodec.decode(
          data.organisation.preferences
        )
        const preferences = isRight(preferencesCodec)
          ? preferencesCodec.right
          : {}

        resetState({
          preferences: {
            inactivityTimeout: getInactivityTimeout(
              data.organisation.id,
              preferences
            ),
            mfaRequired: preferences.mfaRequired,
            showFullJWTInTools: preferences.showFullJWTInTools
          }
        })
      }
    },
    [resetState]
  )

  useEffect(() => {
    setFormState(orgQuery.data)
  }, [orgQuery.data, setFormState])

  return (
    <>
      <header>
        <IconHeading>
          <Icon name="padlock" />
          <a href="#mapping">SDK request debugger</a>
        </IconHeading>
      </header>
      <ConstrainWidth>
        <LightText>
          Enabling this setting will show all JWT claims in a given SDK request.
          Depending on the content in the JWT this may include sensitive
          information.
        </LightText>
        <Form
          loading={loading}
          onCancel={changed ? () => setFormState(orgQuery.data) : undefined}
          onSubmit={() => update(localState.preferences)}
          error={error}
          submitText="Save"
          isValid={changed && !loading}
        >
          <Switch
            disabled={!canEdit}
            label="Show all JWT claims in SDK request debugger tool"
            isForm={true}
            labelPosition="left"
            checked={!!localState.preferences.showFullJWTInTools}
            onChange={showFullJWTInTools =>
              setLocalState(c => {
                return {
                  preferences: {
                    ...c.preferences,
                    showFullJWTInTools
                  }
                }
              })
            }
          />
        </Form>
      </ConstrainWidth>
    </>
  )
}

const ManageAuthentication = ({
  organisationId,
  canEdit
}: {
  organisationId: string
  canEdit: boolean
}) => {
  const toaster = useToast()
  const orgQuery = useGetOrganisationQuery({variables: {id: organisationId}})

  const [localState, setLocalState, changed, resetState] = useStateChanged<{
    preferences: OrganisationPreferences
  }>({
    preferences: {}
  })

  // MFA Confirm Modal
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const [doMutation, {loading, error}] =
    useOrganisationManagePreferencesMutation({
      onCompleted: () => toaster('Organization settings updated')
    })

  const update = (save: OrganisationPreferences) =>
    doMutation({
      variables: {
        id: organisationId,
        updates: save
      }
    })

  const setFormState = useCallback(
    (data: (typeof orgQuery)['data']) => {
      if (data?.organisation?.name) {
        const preferencesCodec = OrganisationPreferencesCodec.decode(
          data.organisation.preferences
        )
        const preferences = isRight(preferencesCodec)
          ? preferencesCodec.right
          : {}

        resetState({
          preferences: {
            inactivityTimeout: getInactivityTimeout(
              data.organisation.id,
              preferences
            ),
            mfaRequired: preferences.mfaRequired,
            showFullJWTInTools: preferences.showFullJWTInTools
          }
        })
      }
    },
    [resetState]
  )

  const onClickUpdate = () => {
    if (
      localState.preferences.mfaRequired !==
      get(orgQuery?.data?.organisation?.preferences, 'mfaRequired')
    ) {
      setShowConfirmModal(true)
    } else {
      update(localState.preferences)
    }
  }

  useEffect(() => {
    setFormState(orgQuery.data)
  }, [orgQuery.data, setFormState])

  return (
    <>
      <header>
        <IconHeading>
          <Icon name="padlock" />
          <a href="#mapping">Authentication preferences</a>
        </IconHeading>
      </header>
      <ConstrainWidth>
        {showConfirmModal && (
          <ConfirmationModal
            name={orgQuery.data?.organisation?.name ?? organisationId}
            mfaRequired={!!localState.preferences.mfaRequired}
            onCancel={() => setShowConfirmModal(false)}
            onSubmit={() => {
              update(localState.preferences)
              setShowConfirmModal(false)
            }}
          />
        )}
        <Form
          loading={loading}
          onCancel={changed ? () => setFormState(orgQuery.data) : undefined}
          onSubmit={onClickUpdate}
          error={error}
          submitText="Save"
          isValid={changed && !loading}
        >
          <Switch
            disabled={!canEdit}
            label="Multi-factor authentication required"
            isForm={true}
            labelPosition="left"
            checked={!!localState.preferences.mfaRequired}
            onChange={mfaRequired =>
              setLocalState(c => {
                return {
                  preferences: {
                    ...c.preferences,
                    mfaRequired
                  }
                }
              })
            }
          />
          <FormSpacer />
          <NumberInput
            readOnly={!canEdit}
            label="Minutes of inactivity before a user is logged out"
            min={'1'}
            value={
              localState.preferences.inactivityTimeout
                ? localState.preferences.inactivityTimeout / 60
                : undefined
            }
            onChange={inactivityMinutes =>
              setLocalState(c => {
                return {
                  preferences: {
                    ...c.preferences,
                    inactivityTimeout: inactivityMinutes
                      ? inactivityMinutes * 60
                      : undefined
                  }
                }
              })
            }
          />
        </Form>
      </ConstrainWidth>
    </>
  )
}

gql`
  mutation OrganisationManagePreferences($id: String!, $updates: JSON!) {
    organisationUpdatePreferences(
      input: {organisationId: $id, updates: $updates}
    ) {
      organisation {
        id
        name
      }
    }
  }
`

const ManageOrganisation = ({
  organisationId,
  canEdit
}: {
  organisationId: string
  canEdit: boolean
}) => {
  const toaster = useToast()
  const orgQuery = useGetOrganisationQuery({variables: {id: organisationId}})
  const analytics = useAnalytics()

  const [localState, setLocalState, changed, resetState] = useStateChanged<{
    name: string
  }>({
    name: orgQuery.data?.organisation?.name ?? ''
  })

  const setFormState = useCallback(
    (data: (typeof orgQuery)['data']) => {
      if (data?.organisation?.name) {
        resetState({name: data?.organisation?.name})
      }
    },
    [resetState]
  )

  const [doMutation, {loading, error}] =
    useManageOrganisationUpdateNameMutation({
      onCompleted: resp => {
        analytics.track({
          event: 'Updated org name',
          properties: {
            category: 'admin',
            org_id: organisationId,
            org_name: resp.updateOrganisation?.organisation?.name
          }
        })

        toaster('Organization name updated')
      }
    })

  const updateName = (save: {name: string}) =>
    doMutation({
      variables: {
        id: organisationId,
        name: save.name
      }
    })

  useEffect(() => {
    setFormState(orgQuery.data)
  }, [orgQuery.data, setFormState])

  return (
    <>
      <header>
        <IconHeading>Organization name</IconHeading>
      </header>
      <ConstrainWidth>
        <Form
          loading={loading}
          onCancel={changed ? () => setFormState(orgQuery.data) : undefined}
          onSubmit={() => updateName(localState)}
          error={error}
          submitText="Save"
          isValid={changed && !loading}
        >
          <TextInput
            style={{maxWidth: '100%', marginBottom: '20px'}}
            disabled={!canEdit}
            label="Give it a name..."
            upLabel="Organization name"
            value={localState.name || ''}
            onChange={name => setLocalState(c => ({...c, name}))}
          />
        </Form>
      </ConstrainWidth>
    </>
  )
}

gql`
  mutation ManageOrganisationUpdateName($id: String!, $name: String) {
    updateOrganisation(input: {id: $id, patch: {name: $name}}) {
      organisation {
        id
        name
      }
    }
  }
`
